/**
 * @prop --color: Color of section
 * @prop --background-color: Background color of section
 * @prop --padding-left: Left padding of section
 * @prop --padding-right: Right padding of section
 * @prop --padding-top: Top padding of section
 * @prop --padding-bottom: Bottom padding section
 *
 * @prop --stzh-section-highlight-color: **Global**: Color of section when highlight variant is used
 * @prop --stzh-section-highlight-background-color: **Global**: Background color of section when highlight variant is used
 */

:host {
  --color: #{$colorBlack};
  --background-color: transparent;
  --heading-color: var(--stzh-section-heading-color, #{$headingColor});
  --heading-background-color: transparent;

  --padding-left: var(--stzh-section-padding-left, 0px);
  --padding-right: var(--stzh-section-padding-right, 0px);
  --padding-top: var(
    --stzh-section-padding-top,
    #{spaceCurveValue("section-inner")}
  );
  --padding-bottom: var(
    --stzh-section-padding-bottom,
    #{spaceCurveValue("section-inner")}
  );

  --margin-top: var(--stzh-section-margin-top, 0px);
  --margin-bottom: var(--stzh-section-margin-bottom, 0px);

  @include mq($from: small) {
    --padding-top: var(
      --stzh-section-padding-top,
      #{spaceCurveValue("section-inner", "small")}
    );
    --padding-bottom: var(
      --stzh-section-padding-bottom,
      #{spaceCurveValue("section-inner", "small")}
    );
  }

  @include mq($from: large) {
    --padding-top: var(
      --stzh-section-padding-top,
      #{spaceCurveValue("section-inner", "large")}
    );
    --padding-bottom: var(
      --stzh-section-padding-bottom,
      #{spaceCurveValue("section-inner", "large")}
    );
  }

  margin-top: var(--margin-top);
  margin-bottom: var(--margin-bottom);

  ::slotted(stzh-heading) {
    @include spaceCurve("margin-top", "small");
    @include spaceCurve("margin-bottom", "small");
    --stzh-heading-color: var(--heading-color);
  }
}

/* Breakout */

:host(:where([breakout]:not([breakout="default"]))) {
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: calc(-50vw + var(--stzh-scrollbar-width) / 2);
  margin-right: calc(-50vw + var(--stzh-scrollbar-width) / 2);
  width: calc(100vw - var(--stzh-scrollbar-width));
  max-width: calc(100vw - var(--stzh-scrollbar-width));
}

/* Background variants */

:host(:where([background]:not([background="default"]))) {
  --padding-top: var(
    --stzh-section-background-padding-top,
    #{spaceCurveValue("background-section-inner")}
  );
  --padding-bottom: var(
    --stzh-section-background-padding-bottom,
    #{spaceCurveValue("background-section-inner")}
  );

  --margin-top: var(
    --stzh-section-background-margin-top,
    #{spaceCurveValue("background-section-outer")}
  );
  --margin-bottom: var(
    --stzh-section-background-margin-bottom,
    #{spaceCurveValue("background-section-outer")}
  );

  @include mq($from: small) {
    --padding-top: var(
      --stzh-section-background-padding-top,
      #{spaceCurveValue("background-section-inner", "small")}
    );
    --padding-bottom: var(
      --stzh-section-background-padding-bottom,
      #{spaceCurveValue("background-section-inner", "small")}
    );
  }

  @include mq($from: large) {
    --padding-top: var(
      --stzh-section-background-padding-top,
      #{spaceCurveValue("background-section-inner", "large")}
    );
    --padding-bottom: var(
      --stzh-section-background-padding-bottom,
      #{spaceCurveValue("background-section-inner", "large")}
    );

    --margin-top: var(
      --stzh-section-background-margin-top,
      #{spaceCurveValue("background-section-outer", "large")}
    );
    --margin-bottom: var(
      --stzh-section-background-margin-bottom,
      #{spaceCurveValue("background-section-outer", "large")}
    );
  }
}

:host([background="secondary"]) {
  --background-color: #{$colorSecondary20};
}

:host([background="primary"]) {
  --background-color: #{$colorPrimary70};
}

/* Highlight variant */

:host([variant="highlight"]) {
  @include spaceCurve("--padding-left", "small");
  @include spaceCurve("--padding-right", "small");
  @include spaceCurve("--padding-top", "medium");
  @include spaceCurve("--padding-bottom", "medium");

  --color: #{$sectionHighlightColor};
  --background-color: #{$sectionHighlightBackgroundColor};
  --heading-background-color: #{$colorSecondary40};

  // disable nested section paddings and margins
  --stzh-section-padding-top: 0px;
  --stzh-section-padding-bottom: 0px;
  --stzh-section-margin-top: 0px;
  --stzh-section-margin-bottom: 0px;
}

/* Contextbox variant */

:host([variant="contextbox"]) {
  @include spaceCurve("--padding-left", "regular");
  @include spaceCurve("--padding-right", "regular");
  @include spaceCurve("--padding-top", "regular");
  @include spaceCurve("--padding-bottom", "regular");

  --color: #{$sectionHighlightColor};
  --background-color: #{$sectionHighlightBackgroundColor};
  --heading-background-color: #{$colorSecondary40};

  // disable nested section paddings and margins
  --stzh-section-padding-top: 0px;
  --stzh-section-padding-bottom: 0px;
  --stzh-section-margin-top: 0px;
  --stzh-section-margin-bottom: 0px;

  &:last-child {
    margin-bottom: 0;
  }
}

/* Greybox variant */

:host([variant="greybox"]) {
  --background-color: #{$colorGrey10};
  @include spaceCurve("--padding-left", "large");
  @include spaceCurve("--padding-right", "large");
  @include spaceCurve("--padding-top", "regular");
  @include spaceCurve("--padding-bottom", "regular");

  &:last-child {
    margin-bottom: 0;
  }
}

:host([variant="poimax"]) {
  --background-color: #{$colorGrey10};
  @include spaceCurve("--padding-left", "regular");
  @include spaceCurve("--padding-right", "regular");
  @include spaceCurve("--padding-top", "regular");
  @include spaceCurve("--padding-bottom", "regular");

  &:last-child {
    margin-bottom: 0;
  }
}

/* Margin variants */

:host([margin-top="none"]) {
  --margin-top: 0px;
}

:host([margin-bottom="none"]) {
  --margin-bottom: 0px;
}

/* Padding variants */

:host([padding-top="none"]) {
  --padding-top: 0px;
}

:host([padding-bottom="none"]) {
  --padding-bottom: 0px;
}

.stzh-section {
  background-color: var(--background-color);

  &__heading {
    display: flex;
    flex-direction: column;
    background-color: var(--heading-background-color);
    padding-left: var(--padding-left);
    padding-right: var(--padding-right);
  }

  &__wrapper {
    padding-left: var(--padding-left);
    padding-right: var(--padding-right);
    padding-top: var(--padding-top);
    padding-bottom: var(--padding-bottom);

    --stzh-base-color: var(--color);
  }

  &--is-breakout &__outer {
    @include container;
  }
}
